import {
  AddComment,
  ArrowBack,
  CheckCircleOutline,
  CompareArrows,
  DeleteSweep,
  EditNote,
  Label,
  Memory,
  PlayArrow,
  Science,
  Settings,
  StarOutline,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Rating,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { MessageBar } from "../components/chats/MessageBar";
import { FeedbackSnackbar } from "../components/design/FeedbackSnackbar";
import { MessageItem } from "../components/chats/MessageItem";
import { MessageTypeEnum } from "../data/models/Chat/MessageModel";
import { TypeRoleEnum } from "../data/models/Chat/TypeRoleEnum";
import {
  TestChatLabelModel,
  TestChatSessionModel,
} from "../data/providers/FirestoreProvider";
import { formatChatDate } from "../utils/DateTool";
import {
  dashboardPageSx,
  dashboardPanelNoPaddingSx,
} from "../components/layout/dashboardStyles";
import { useTestChat } from "./hooks/useTestChat";

const LABEL_COLOR_PRESETS = [
  "#1f6feb",
  "#238636",
  "#a371f7",
  "#d29922",
  "#db6d28",
  "#f85149",
  "#8957e5",
  "#0ea5e9",
];

const TEST_LABEL_PRESETS: TestChatLabelModel[] = [
  { id: "delivery", name: "delivery", color: "#1f6feb" },
  { id: "pickup", name: "pickup", color: "#0ea5e9" },
  { id: "pedido-enviado", name: "pedido enviado", color: "#238636" },
  { id: "pedido-no-enviado", name: "pedido no enviado", color: "#f85149" },
  { id: "bug", name: "bug", color: "#db6d28" },
  { id: "prompt", name: "prompt", color: "#a371f7" },
  { id: "historial", name: "historial", color: "#8957e5" },
  { id: "ux", name: "ux", color: "#d29922" },
];

const toAlphaHex = (color: string, alpha: string) =>
  /^#[0-9a-f]{6}$/i.test(color) ? `${color}${alpha}` : color;

const getLabelChipSx = (color: string) => ({
  bgcolor: toAlphaHex(color, "26"),
  borderColor: toAlphaHex(color, "66"),
  color,
  fontWeight: 700,
});

const formatSessionTitle = (createdAt: Date, isActive: boolean) =>
  isActive
    ? "Chat activo"
    : new Intl.DateTimeFormat("es-ES", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(createdAt);

const getSessionDisplayTitle = (
  session: TestChatSessionModel,
  activeServiceId?: string
) =>
  session.testMetadata.evaluation.title.trim() ||
  formatSessionTitle(session.createdAt, session.id === activeServiceId);

const getSessionPreview = (session: TestChatSessionModel) =>
  session.testMetadata.evaluation.description.trim() ||
  session.testMetadata.notes.trim() ||
  session.lastMessage;

const formatDateDivider = (dateString: string) => {
  const parsedDate = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) return "Fecha no válida";
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (parsedDate.toDateString() === today.toDateString()) return "Hoy";
  if (parsedDate.toDateString() === yesterday.toDateString()) return "Ayer";
  return new Intl.DateTimeFormat("es-ES", { dateStyle: "medium" }).format(
    parsedDate
  );
};

const groupMessagesByDate = (messages: any[]) =>
  messages.reduce((groups: Record<string, any[]>, message) => {
    const date = new Date(message.createdAt).toLocaleDateString("sv-SE");
    groups[date] = groups[date] || [];
    groups[date].push(message);
    return groups;
  }, {});

const formatSavedAt = (value: Date | null) =>
  value
    ? new Intl.DateTimeFormat("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(value)
    : "";

const formatReplayRunLabel = (createdAt: Date) =>
  new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(createdAt);

const TestChatPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showMobileConversation, setShowMobileConversation] = useState(false);
  const [replayDrawerOpen, setReplayDrawerOpen] = useState(false);
  const [customLabelName, setCustomLabelName] = useState("");
  const [customLabelColor, setCustomLabelColor] = useState(LABEL_COLOR_PRESETS[0]);

  const {
    activeServiceId,
    addSessionLabel,
    apiBaseUrl,
    apiInputValue,
    canEditSessionMetadata,
    canSendMessages,
    chatDocumentError,
    clearHistory,
    clearSelectedReplayRun,
    company,
    companyConfig,
    configError,
    currentApiPreset,
    displayedMessages,
    endOfMessagesRef,
    isDraftChat,
    isRunningReplay,
    isResetting,
    isSavingMetadata,
    loading,
    mappingOpen,
    messagesError,
    metadataDirty,
    metadataError,
    metadataSavedAt,
    metadataStatusMessage,
    openMapping,
    openSettings,
    removeSessionLabel,
    replayError,
    replayIterations,
    replayMessagesError,
    replayRuns,
    replayRunsError,
    resetApiBaseUrl,
    runSessionReplay,
    saveApiBaseUrl,
    saveSessionMetadata,
    selectedServiceId,
    selectedReplayRun,
    selectedSession,
    selectReplayRun,
    selectSession,
    sessionMetadata,
    sessions,
    sessionsError,
    setApiInputValue,
    setMappingOpen,
    setReplayIterations,
    setSessionEvaluationDescription,
    setSessionEvaluationRating,
    setSessionEvaluationTitle,
    setSessionNotes,
    setSettingsOpen,
    settingsOpen,
    startNewChat,
    testPhoneNumber,
    toggleSessionLabel,
    sendMessage,
    applyApiPreset,
    apiPresets,
  } = useTestChat();

  const groupedMessages = groupMessagesByDate(displayedMessages);
  const hasSelectedView = isDraftChat || Boolean(selectedSession);
  const showSidebar = !isMobile || !showMobileConversation;
  const showConversation = !isMobile || showMobileConversation;
  const pageError =
    configError ||
    sessionsError ||
    messagesError ||
    chatDocumentError;
  const [feedbackSnackbar, setFeedbackSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const selectedLabels = sessionMetadata.labels;
  const evaluation = sessionMetadata.evaluation;

  useEffect(() => {
    if (!pageError) return;
    setFeedbackSnackbar({
      open: true,
      message: pageError,
      severity: "error",
    });
  }, [pageError]);

  useEffect(() => {
    if (metadataError) {
      setFeedbackSnackbar({
        open: true,
        message: metadataError,
        severity: "error",
      });
      return;
    }
    if (metadataStatusMessage) {
      setFeedbackSnackbar({
        open: true,
        message: metadataStatusMessage,
        severity: "success",
      });
    }
  }, [metadataError, metadataStatusMessage]);

  const visiblePresetLabels = useMemo(
    () =>
      TEST_LABEL_PRESETS.map((label) => ({
        ...label,
        isSelected: selectedLabels.some((item) => item.id === label.id),
      })),
    [selectedLabels]
  );

  useEffect(() => {
    if (!isMobile || hasSelectedView) return;
    setShowMobileConversation(false);
  }, [hasSelectedView, isMobile]);

  const handleOpenSession = (session: TestChatSessionModel) => {
    selectSession(session);
    if (isMobile) setShowMobileConversation(true);
  };

  const handleStartNewChat = async () => {
    const isSuccess = await startNewChat();
    if (isSuccess && isMobile) setShowMobileConversation(true);
  };

  const handleClearHistory = async () => {
    const isSuccess = await clearHistory();
    if (isSuccess && isMobile) setShowMobileConversation(true);
  };

  const handleAddCustomLabel = () => {
    const name = customLabelName.trim();
    if (!name) return;
    addSessionLabel({
      id: name.toLowerCase().replace(/\s+/g, "-"),
      name,
      color: customLabelColor,
    });
    setCustomLabelName("");
  };

  const sessionTitle = isDraftChat
    ? evaluation.title.trim() || "Nuevo chat"
    : selectedReplayRun
    ? `Replay ${formatChatDate(selectedReplayRun.createdAt)}`
    : selectedSession
    ? getSessionDisplayTitle(selectedSession, activeServiceId)
    : "Selecciona un chat";

  const sessionSubtitle = isDraftChat
    ? "Listo para iniciar una conversación nueva"
    : selectedReplayRun
    ? "Resultado re-ejecutado automáticamente para comparar tools y respuestas"
    : selectedSession
    ? selectedSession.id === activeServiceId
      ? "Sesión activa del laboratorio"
      : "Historial de una sesión anterior"
    : "Elige una sesión de prueba del panel izquierdo";

  return (
    <>
      <Box
        sx={{
          ...dashboardPageSx,
          display: "flex",
          flex: 1,
          height: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
        flexDirection={{ xs: "column", md: "row" }}
      >
        {showSidebar && (
          <Box
            width={{ xs: "100%", md: "30%" }}
            bgcolor="background.paper"
            overflow="auto"
            sx={{
              ...dashboardPanelNoPaddingSx,
              borderTop: 0,
              borderLeft: 0,
              flex: { xs: 1, md: "0 0 30%" },
              minHeight: 0,
              minWidth: { md: 360 },
              p: 2,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Test Chat
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Historial aislado del laboratorio
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Mapear sesión">
                  <span>
                    <IconButton onClick={openMapping} disabled={!canEditSessionMetadata}>
                      <EditNote />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Configurar API">
                  <IconButton onClick={openSettings}>
                    <Settings />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" mb={2} useFlexGap>
              <Chip size="small" icon={<Science />} label={company || "Sin empresa"} color="primary" />
              <Chip size="small" icon={<Memory />} label={currentApiPreset.toUpperCase()} variant="outlined" />
              <Chip size="small" label={testPhoneNumber} variant="outlined" />
            </Stack>

            <Stack direction="row" spacing={1} mb={2}>
              <Button variant="contained" startIcon={<AddComment />} onClick={handleStartNewChat} disabled={isResetting} fullWidth>
                Nuevo chat
              </Button>
              <Tooltip title="Borrar historial del laboratorio">
                <span>
                  <Button variant="outlined" color="inherit" onClick={handleClearHistory} disabled={isResetting} sx={{ minWidth: 0, px: 1.5 }}>
                    <DeleteSweep />
                  </Button>
                </span>
              </Tooltip>
            </Stack>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                <ListItemButton
                  selected={isDraftChat}
                  onClick={handleStartNewChat}
                  sx={{
                    mb: 1,
                    borderRadius: 1,
                    border: "1px dashed",
                    borderColor: isDraftChat ? "primary.main" : "divider",
                    bgcolor: isDraftChat ? "action.selected" : "transparent",
                  }}
                >
                  <Avatar sx={{ mr: 2, bgcolor: isDraftChat ? "primary.main" : "grey.700" }}>
                    <AddComment />
                  </Avatar>
                  <ListItemText
                    primary="Nuevo chat"
                    secondary="Prepara una conversación limpia sin borrar el historial."
                    primaryTypographyProps={{ fontWeight: 700 }}
                  />
                </ListItemButton>

                {sessions.map((session) => {
                  const isSelected = selectedServiceId === session.id && !isDraftChat;
                  const isActive = activeServiceId === session.id;
                  return (
                    <ListItemButton
                      key={session.id}
                    onClick={() => handleOpenSession(session)}
                    selected={isSelected}
                    sx={{
                      mb: 1,
                      borderRadius: 1,
                      alignItems: "flex-start",
                      border: "1px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                    }}
                  >
                      <Avatar sx={{ mr: 2, bgcolor: isActive ? "success.main" : "grey.700" }}>
                        {isActive ? "A" : "H"}
                      </Avatar>
                      <Box width="100%" overflow="hidden">
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                          <Typography variant="body1" fontWeight={700} noWrap sx={{ maxWidth: "70%" }}>
                            {getSessionDisplayTitle(session, activeServiceId)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatChatDate(session.updatedAt)}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {getSessionPreview(session)}
                        </Typography>
                        <Stack direction="row" spacing={0.75} mt={1} flexWrap="wrap" useFlexGap>
                          {session.testMetadata.evaluation.rating > 0 && (
                            <Chip size="small" icon={<StarOutline sx={{ fontSize: 14 }} />} label={`${session.testMetadata.evaluation.rating}/10`} variant="outlined" />
                          )}
                          {session.testMetadata.labels.slice(0, 2).map((label) => (
                            <Chip key={label.id} size="small" label={label.name} variant="outlined" sx={getLabelChipSx(label.color)} />
                          ))}
                          {isActive && <Chip size="small" label="Sesión activa" color="success" variant="outlined" />}
                        </Stack>
                      </Box>
                    </ListItemButton>
                  );
                })}

                {!sessions.length && !isDraftChat && (
                  <Box py={6} textAlign="center" color="text.secondary">
                    <Typography variant="body1" fontWeight={700}>
                      No hay historial todavía
                    </Typography>
                    <Typography variant="body2">
                      Inicia un chat de prueba para comenzar a guardar sesiones.
                    </Typography>
                  </Box>
                )}
              </List>
            )}
          </Box>
        )}

        {!isMobile && <Divider flexItem orientation="vertical" />}

        {showConversation && (
        <Box
          bgcolor="background.paper"
          flex={1}
          display="flex"
          flexDirection="column"
          sx={{
            ...dashboardPanelNoPaddingSx,
            borderTop: 0,
            p: 0,
            minHeight: 0,
          }}
        >
            <Box display="flex" alignItems="center" justifyContent="space-between" p={2} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                {isMobile && hasSelectedView && (
                  <IconButton onClick={() => setShowMobileConversation(false)} sx={{ display: { xs: "inline-flex", md: "none" } }}>
                    <ArrowBack />
                  </IconButton>
                )}
                <Avatar sx={{ bgcolor: isDraftChat ? "primary.main" : "grey.700" }}>
                  {isDraftChat ? <AddComment /> : "T"}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>{sessionTitle}</Typography>
                  <Typography variant="subtitle2" color="text.secondary">{sessionSubtitle}</Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={currentApiPreset.toUpperCase()} />
                {selectedSession && !isDraftChat && (
                  <Tooltip title="Abrir replays">
                    <IconButton onClick={() => setReplayDrawerOpen(true)}>
                      <PlayArrow />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Mapear sesión">
                  <span>
                    <IconButton onClick={openMapping} disabled={!canEditSessionMetadata}>
                      <EditNote />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Configurar API">
                  <IconButton onClick={openSettings}>
                    <Settings />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {!hasSelectedView ? (
              <Box flex={1} display="flex" flexDirection="column" justifyContent="center" alignItems="center" textAlign="center" sx={{ color: "text.secondary", opacity: 0.7 }}>
                <Typography variant="h6">Selecciona un chat de prueba</Typography>
                <Typography variant="body2">Aquí verás el historial completo de la sesión elegida.</Typography>
              </Box>
            ) : (
              <>
                {selectedSession && !isDraftChat && selectedSession.id !== activeServiceId && (
                  <Box
                    sx={{
                      px: 2,
                      py: 1.1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      bgcolor: "rgba(2, 136, 209, 0.08)",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={0.75}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                    >
                      <Chip
                        size="small"
                        label="Modo lectura"
                        color="info"
                        variant="outlined"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Estás viendo una sesión histórica. Para seguir probando,
                        abre un chat nuevo o vuelve a la sesión activa.
                      </Typography>
                    </Stack>
                  </Box>
                )}

                {selectedReplayRun && (
                  <Paper
                    variant="outlined"
                    sx={{ mx: 2, mt: 2, p: 1.25, borderRadius: 1 }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          Viendo replay: {formatReplayRunLabel(selectedReplayRun.createdAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedReplayRun.status === "failed"
                            ? selectedReplayRun.error || "El replay falló."
                            : selectedReplayRun.exactToolMatch
                            ? "La secuencia de tools coincidió con la original."
                            : `Cambió la secuencia de tools. Faltantes: ${selectedReplayRun.missingTools.join(", ") || "ninguno"}.`}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          label={
                            selectedReplayRun.status === "failed"
                              ? "Falló"
                              : "Completado"
                          }
                          color={
                            selectedReplayRun.status === "failed"
                              ? "error"
                              : "success"
                          }
                          variant="outlined"
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setReplayDrawerOpen(true)}
                        >
                          Abrir panel
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                )}

                <Paper variant="outlined" sx={{ mx: 2, mt: 2, p: 1.5, borderRadius: 1 }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between">
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight={700}>
                        {evaluation.title.trim() || "Sin título de referencia"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {evaluation.description.trim() || sessionMetadata.notes.trim() || "Agrega notas, contexto y evaluacion para mapear esta prueba."}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {evaluation.rating > 0 && <Chip size="small" icon={<StarOutline sx={{ fontSize: 14 }} />} label={`${evaluation.rating}/10`} variant="outlined" />}
                      {evaluation.finalizedAt && <Chip size="small" icon={<CheckCircleOutline sx={{ fontSize: 14 }} />} label="Evaluacion finalizada" color="success" variant="outlined" />}
                      {metadataDirty && <Chip size="small" label="Cambios sin guardar" color="warning" variant="outlined" />}
                      {!metadataDirty && metadataSavedAt && <Chip size="small" label={`Guardado ${formatSavedAt(metadataSavedAt)}`} color="success" variant="outlined" />}
                      <Button size="small" variant="outlined" startIcon={<EditNote />} onClick={openMapping} disabled={!canEditSessionMetadata}>
                        Mapear
                      </Button>
                    </Stack>
                  </Stack>
                  {selectedLabels.length > 0 && (
                    <Stack direction="row" spacing={0.75} mt={1.5} flexWrap="wrap" useFlexGap>
                      {selectedLabels.map((label) => (
                        <Chip key={label.id} size="small" label={label.name} variant="outlined" sx={getLabelChipSx(label.color)} />
                      ))}
                    </Stack>
                  )}

                  {selectedSession && !isDraftChat && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", sm: "center" }}
                      >
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${replayRuns.length} replays`}
                          />
                          {selectedReplayRun && (
                            <Chip
                              size="small"
                              color={
                                selectedReplayRun.status === "failed"
                                  ? "error"
                                  : selectedReplayRun.exactToolMatch
                                  ? "success"
                                  : "warning"
                              }
                              variant="outlined"
                              label="Replay activo"
                            />
                          )}
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          {selectedReplayRun && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CompareArrows />}
                              onClick={clearSelectedReplayRun}
                            >
                              Ver original
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PlayArrow />}
                            onClick={() => setReplayDrawerOpen(true)}
                          >
                            Abrir replays
                          </Button>
                        </Stack>
                      </Stack>
                    </>
                  )}
                </Paper>

                <Box
                  flex={1}
                  overflow="auto"
                  display="flex"
                  flexDirection="column-reverse"
                  gap={1.5}
                  p={2}
                  sx={{ bgcolor: "background.default" }}
                >
                  {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <CircularProgress />
                    </Box>
                  ) : displayedMessages.length > 0 ? (
                    Object.entries(groupedMessages).reverse().map(([date, messages]) => (
                      <React.Fragment key={date}>
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Box position="sticky" top={0} zIndex={1} display="flex" justifyContent="center" my={1}>
                            <Typography variant="subtitle2" align="center" sx={{ display: "inline-block", backgroundColor: "text.secondary", px: 1.5, py: 0.5, borderRadius: 4, boxShadow: 1, color: "background.default" }}>
                              {formatDateDivider(date)}
                            </Typography>
                          </Box>
                          {(messages as any[]).map((message, index) => {
                            const isAssistantSide =
                              message.role === TypeRoleEnum.SYSTEM ||
                              message.role === TypeRoleEnum.ASSISTANT ||
                              message.role === TypeRoleEnum.TOOL;
                            const isImageMessage =
                              message.messageType === MessageTypeEnum.IMAGE;
                            return (
                              <Box key={`${date}-${index}`} display="flex" flexDirection="column" alignItems={isAssistantSide ? "flex-end" : "flex-start"}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: isImageMessage ? 0.45 : 1.5,
                                    borderRadius: "18px",
                                    borderBottomRightRadius: isAssistantSide ? "4px" : "18px",
                                    borderBottomLeftRadius: isAssistantSide ? "18px" : "4px",
                                    bgcolor: isAssistantSide ? "grey.200" : "primary.main",
                                    color: isAssistantSide ? "black" : "white",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    maxWidth: "70%",
                                  }}
                                >
                                  <MessageItem message={message} />
                                </Paper>
                                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 12, mt: 0.5 }}>
                                  {new Intl.DateTimeFormat("es-ES", { hour: "2-digit", minute: "2-digit" }).format(new Date(message.createdAt))}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                        <div ref={endOfMessagesRef} style={{ height: 0, visibility: "hidden" }} />
                      </React.Fragment>
                    ))
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography variant="body2" color="text.secondary">
                        {isDraftChat ? "Este chat está listo. Envia el primer mensaje para crear una nueva sesión." : "No hay mensajes en esta sesión."}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box p={1}>
                  {canSendMessages ? (
                    <MessageBar
                      onSend={async (text: string) => sendMessage({ type: MessageTypeEnum.TEXT, content: text })}
                      onAttach={async (file: File) => sendMessage({ type: MessageTypeEnum.IMAGE, file })}
                      onVoice={async (blob: Blob) => sendMessage({ type: MessageTypeEnum.AUDIO, blob })}
                      onEmoji={() => undefined}
                    />
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={700}>Historial en modo lectura</Typography>
                        <Typography variant="caption" color="text.secondary">
                          La escritura solo está habilitada en la sesión activa o en un chat nuevo.
                        </Typography>
                      </Box>
                      <Button variant="contained" startIcon={<AddComment />} onClick={handleStartNewChat} disabled={isResetting}>
                        Nuevo chat
                      </Button>
                    </Paper>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 380 }, p: 2 } }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Conexión del laboratorio
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cambia rápido la API a la que apuntan los mensajes de prueba.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Empresa: ${company || "Sin empresa"}`} />
            <Chip label={`Número: ${testPhoneNumber}`} variant="outlined" />
          </Stack>

          <TextField
            label="Base URL de la API"
            value={apiInputValue}
            onChange={(event) => setApiInputValue(event.target.value)}
            fullWidth
            size="small"
            placeholder="https://api-server-dev-xxxx.run.app"
            helperText={`Actual: ${apiBaseUrl}`}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant={currentApiPreset === "local" ? "contained" : "outlined"} onClick={() => applyApiPreset(apiPresets.local)}>
              Local
            </Button>
            <Button variant={currentApiPreset === "dev" ? "contained" : "outlined"} onClick={() => applyApiPreset(apiPresets.dev)}>
              Dev
            </Button>
            <Button variant={currentApiPreset === "prod" ? "contained" : "outlined"} onClick={() => applyApiPreset(apiPresets.prod)}>
              Prod
            </Button>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={saveApiBaseUrl} fullWidth>
              Guardar URL
            </Button>
            <Button variant="outlined" onClick={resetApiBaseUrl} fullWidth>
              Restablecer
            </Button>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Configuración detectada
            </Typography>
            <Typography variant="body2" color="text.secondary">
              phoneNumberId: {companyConfig?.wsSettings.phoneNumberId || "No configurado"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              whatsappId: {companyConfig?.wsSettings.whatsappId || "No configurado"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              accessToken: {companyConfig?.wsSettings.accessTokenMasked || "No configurado"}
            </Typography>
          </Box>

          <Divider />

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<AddComment />}
              onClick={async () => {
                await handleStartNewChat();
                setSettingsOpen(false);
              }}
              disabled={isResetting}
              fullWidth
            >
              Nuevo chat
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweep />}
              onClick={async () => {
                await handleClearHistory();
                setSettingsOpen(false);
              }}
              disabled={isResetting}
              fullWidth
            >
              Borrar historial
            </Button>
          </Stack>
        </Stack>
      </Drawer>

      <Drawer
        anchor="right"
        open={replayDrawerOpen}
        onClose={() => setReplayDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 420 }, p: 2 },
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Replays del chat
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Reejecuta la sesión histórica sin tapar el transcript principal.
            </Typography>
          </Box>

          {!selectedSession || isDraftChat ? (
            <Alert severity="info">
              Abre una sesión histórica para lanzar replays automáticos.
            </Alert>
          ) : (
            <>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={getSessionDisplayTitle(selectedSession, activeServiceId)} />
                <Chip size="small" variant="outlined" label={`${replayRuns.length} replays`} />
                {selectedReplayRun && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={
                      selectedReplayRun.status === "failed"
                        ? "error"
                        : selectedReplayRun.exactToolMatch
                        ? "success"
                        : "warning"
                    }
                    label="Replay activo"
                  />
                )}
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <TextField
                  label="Repeticiones"
                  type="number"
                  size="small"
                  value={replayIterations}
                  onChange={(event) =>
                    setReplayIterations(
                      Math.max(1, Math.min(5, Number(event.target.value) || 1))
                    )
                  }
                  inputProps={{ min: 1, max: 5 }}
                  sx={{ width: { xs: "100%", sm: 140 } }}
                />
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={runSessionReplay}
                  disabled={isRunningReplay}
                >
                  {isRunningReplay ? "Reprobando..." : "Correr replay"}
                </Button>
                {selectedReplayRun && (
                  <Button
                    variant="outlined"
                    startIcon={<CompareArrows />}
                    onClick={clearSelectedReplayRun}
                  >
                    Ver original
                  </Button>
                )}
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Cada corrida compara tools, estado final y deja un transcript nuevo
                para revisar en el chat.
              </Typography>

              {(replayError || replayRunsError || replayMessagesError) && (
                <Alert severity="error">
                  {replayError || replayRunsError || replayMessagesError}
                </Alert>
              )}

              {selectedReplayRun && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Chip
                        size="small"
                        label={
                          selectedReplayRun.status === "failed"
                            ? "Falló"
                            : "Completado"
                        }
                        color={
                          selectedReplayRun.status === "failed"
                            ? "error"
                            : "success"
                        }
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={
                          selectedReplayRun.exactToolMatch
                            ? "Tools iguales"
                            : "Tools distintos"
                        }
                        color={
                          selectedReplayRun.exactToolMatch
                            ? "success"
                            : "warning"
                        }
                        variant="outlined"
                      />
                      {selectedReplayRun.stateDifferences.length > 0 && (
                        <Chip
                          size="small"
                          label={`${selectedReplayRun.stateDifferences.length} dif. de estado`}
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    <Typography variant="body2" fontWeight={700}>
                      Replay activo: {formatReplayRunLabel(selectedReplayRun.createdAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedReplayRun.replayToolSequence.length
                        ? selectedReplayRun.replayToolSequence.join(" -> ")
                        : "Sin tools registradas"}
                    </Typography>
                  </Stack>
                </Paper>
              )}

              {replayRuns.length > 0 ? (
                <Stack spacing={1} sx={{ overflow: "auto" }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Corridas recientes
                  </Typography>
                  {replayRuns.map((run) => {
                    const isSelectedReplay = selectedReplayRun?.id === run.id;
                    const replaySeverity =
                      run.status === "failed"
                        ? "error"
                        : run.exactToolMatch && run.stateDifferences.length === 0
                        ? "success"
                        : "warning";

                    return (
                      <Paper
                        key={run.id}
                        variant="outlined"
                        onClick={() => selectReplayRun(run)}
                        sx={{
                          p: 1.25,
                          borderRadius: 1,
                          cursor: "pointer",
                          borderColor: isSelectedReplay
                            ? "primary.main"
                            : "divider",
                          bgcolor: isSelectedReplay
                            ? "action.selected"
                            : "transparent",
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography variant="body2" fontWeight={700}>
                              {formatReplayRunLabel(run.createdAt)}
                            </Typography>
                            <Chip
                              size="small"
                              label={
                                run.status === "failed" ? "Falló" : "Completado"
                              }
                              color={
                                run.status === "failed" ? "error" : "success"
                              }
                              variant="outlined"
                            />
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Chip
                              size="small"
                              label={
                                run.exactToolMatch
                                  ? "Tools iguales"
                                  : "Tools distintos"
                              }
                              color={replaySeverity}
                              variant="outlined"
                            />
                            {run.stateDifferences.length > 0 && (
                              <Chip
                                size="small"
                                label={`${run.stateDifferences.length} dif. de estado`}
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {run.replayToolSequence.length
                              ? run.replayToolSequence.join(" -> ")
                              : "Sin tools registradas"}
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <Alert severity="info">
                  Aún no hay corridas para esta sesión.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </Drawer>

      <Drawer
        anchor="right"
        open={mappingOpen}
        onClose={() => setMappingOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 440 }, p: 2 } }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Mapeo de la sesión
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Guarda notas, referencia funcional, calificación y etiquetas del caso.
            </Typography>
          </Box>

          {isDraftChat && (
            <Alert severity="info">
              Puedes preparar el mapeo desde ya. Se guardara cuando envies el
              primer mensaje del chat.
            </Alert>
          )}
          {!canEditSessionMetadata && (
            <Alert severity="warning">
              Abre un chat o crea una sesión nueva para empezar a mapear.
            </Alert>
          )}
          <TextField
            label="Notas del tester"
            placeholder="Observacíones, respuestas raras, prompts útiles, datos del negocio..."
            value={sessionMetadata.notes}
            onChange={(event) => setSessionNotes(event.target.value)}
            multiline
            minRows={4}
            disabled={!canEditSessionMetadata || isSavingMetadata}
          />

          <TextField
            label="Título de referencia"
            placeholder="Pedido delivery con transferencia"
            value={evaluation.title}
            onChange={(event) => setSessionEvaluationTitle(event.target.value)}
            size="small"
            disabled={!canEditSessionMetadata || isSavingMetadata}
          />

          <TextField
            label="Descripción de referencia"
            placeholder="Qué querías validar y que terminó pasando..."
            value={evaluation.description}
            onChange={(event) => setSessionEvaluationDescription(event.target.value)}
            multiline
            minRows={3}
            disabled={!canEditSessionMetadata || isSavingMetadata}
          />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2" fontWeight={700}>
                Calificacion final
              </Typography>
              <Chip size="small" icon={<StarOutline sx={{ fontSize: 14 }} />} label={`${evaluation.rating || 0}/10`} variant="outlined" />
            </Stack>
            <Rating
              max={10}
              value={evaluation.rating}
              onChange={(_, value) => setSessionEvaluationRating(value || 0)}
              disabled={!canEditSessionMetadata || isSavingMetadata}
            />
            {evaluation.finalizedAt && (
              <Chip
                size="small"
                icon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
                label={`Finalizada ${formatChatDate(evaluation.finalizedAt)}`}
                color="success"
                variant="outlined"
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Labels rápidos
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {visiblePresetLabels.map((label) => (
                <Chip
                  key={label.id}
                  label={label.name}
                  onClick={() => toggleSessionLabel(label)}
                  variant={label.isSelected ? "filled" : "outlined"}
                  sx={getLabelChipSx(label.color)}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Label personalizada
            </Typography>
            <TextField
              label="Nombre del label"
              value={customLabelName}
              onChange={(event) => setCustomLabelName(event.target.value)}
              size="small"
              disabled={!canEditSessionMetadata || isSavingMetadata}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAddCustomLabel();
                }
              }}
            />
            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
              {LABEL_COLOR_PRESETS.map((color) => (
                <Box
                  key={color}
                  component="button"
                  type="button"
                  onClick={() => {
                    if (!canEditSessionMetadata || isSavingMetadata) return;
                    setCustomLabelColor(color);
                  }}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "999px",
                    border: "2px solid",
                    borderColor: customLabelColor === color ? "common.white" : color,
                    bgcolor: color,
                    cursor:
                      !canEditSessionMetadata || isSavingMetadata
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      !canEditSessionMetadata || isSavingMetadata ? 0.5 : 1,
                  }}
                />
              ))}
            </Stack>
            <Button
              variant="outlined"
              startIcon={<Label />}
              onClick={handleAddCustomLabel}
              disabled={!canEditSessionMetadata || isSavingMetadata || !customLabelName.trim()}
              sx={{ mt: 1.5 }}
            >
              Agregar label
            </Button>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Labels aplicados
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {selectedLabels.length ? (
                selectedLabels.map((label) => (
                  <Chip
                    key={label.id}
                    label={label.name}
                    onDelete={() => removeSessionLabel(label.id)}
                    variant="outlined"
                    sx={getLabelChipSx(label.color)}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aún no has agregado etiquetas a esta sesión.
                </Typography>
              )}
            </Stack>
          </Box>

          <Divider />

          <Stack spacing={1}>
            <Button variant="contained" onClick={() => saveSessionMetadata()} disabled={!canEditSessionMetadata || isSavingMetadata} fullWidth>
              {isSavingMetadata ? "Guardando..." : "Guardar mapeo"}
            </Button>
            <Button variant="outlined" color="success" onClick={() => saveSessionMetadata({ finalize: true })} disabled={!canEditSessionMetadata || isSavingMetadata} fullWidth>
              Finalizar y calificar
            </Button>
            {metadataDirty && (
              <Typography variant="caption" color="warning.main">
                Hay cambios pendientes por guardar.
              </Typography>
            )}
          </Stack>
        </Stack>
      </Drawer>

      <FeedbackSnackbar
        open={feedbackSnackbar.open}
        message={feedbackSnackbar.message}
        severity={feedbackSnackbar.severity}
        onClose={() =>
          setFeedbackSnackbar((current) => ({
            ...current,
            open: false,
          }))
        }
      />
    </>
  );
};

export default TestChatPage;
